import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, Loader2, PackageSearch } from "lucide-react";

export default function Track() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [id, setId] = useState(paramId || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (rid) => {
    if (!rid?.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/track/${rid.trim()}`);
      setResult(data);
    } catch (e) {
      setError(e.response?.status === 404 ? "No report found with that ID." : "Lookup failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (paramId) lookup(paramId); }, [paramId]);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/track/${id.trim()}`);
    lookup(id);
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-16 sm:px-6">
      <div className="text-center">
        <PackageSearch className="mx-auto text-[#00F0FF]" size={34} />
        <h1 className="mt-3 font-heading text-3xl font-black tracking-tight text-white">Track a Report</h1>
        <p className="mt-2 text-[#94A3B8]">Paste a report tracking ID to see where it stands in the vetting process.</p>
      </div>

      <form onSubmit={submit} className="mt-8 flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input data-testid="track-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="Report ID…" className="border-[#1E293F] bg-[#141B2D] pl-9 text-white" />
        </div>
        <Button data-testid="track-btn" type="submit" disabled={loading} className="bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Track"}
        </Button>
      </form>

      {error && <p data-testid="track-error" className="mt-4 text-center text-sm text-[#FF2A55]">{error}</p>}

      {result && (
        <div data-testid="track-result" className="mt-6 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(`/players/${result.platform}/${encodeURIComponent(result.gamertag)}`)} className="font-heading text-2xl font-bold text-white hover:text-[#00F0FF]">{result.gamertag}</button>
            <StatusBadge status={result.status} testid="track-status" />
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-[#94A3B8]">
            <span className="text-[#00F0FF]">{result.platform}</span> · <span className="text-[#FF2A55]">{result.cheat_type}</span>
          </div>
          <div className="mt-4 space-y-1.5 border-t border-[#1E293F] pt-4 text-sm text-[#94A3B8]">
            <div>Submitted: {new Date(result.created_at).toLocaleString()}</div>
            {result.vetted_at && <div>Reviewed: {new Date(result.vetted_at).toLocaleString()} {result.vetted_by && `by ${result.vetted_by}`}</div>}
            {result.mod_note && <div className="text-[#CBD5E1]">Modder note: {result.mod_note}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
